<template>
  <div class="space-y-6">
    <header class="flex items-center justify-between pt-12">
      <div>
        <h1 class="text-2xl font-semibold text-white">Equipment Catalog</h1>
        <p class="text-sm text-slate-400 mt-1">
          Review and update active equipment types across the platform.
        </p>
      </div>
      <div class="flex items-center gap-3">
        <button class="btn-secondary btn-secondary--outline" @click="openBulkUpload">Bulk Upload</button>
        <button class="btn-primary flex items-center gap-2" @click="openCreate">
          <span class="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-500/20 text-primary-200">+</span>
          Add Equipment Type
        </button>
        <router-link to="/admin-equipment-drafts" class="btn-secondary btn-secondary--outline">
          Manage Drafts
        </router-link>
      </div>
    </header>

    <section class="panel-neo panel-neo--glassier p-6 rounded-2xl space-y-4">
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex items-center gap-2">
          <label class="text-xs uppercase tracking-[0.2em] text-slate-400">Search</label>
          <input
            v-model="search"
            type="text"
            placeholder="Search by name or descriptor"
            class="input-dark w-80"
          />
        </div>
        <div class="flex items-center gap-2 text-xs text-slate-400">
          <span>Status:</span>
          <button
            v-for="option in statusFilters"
            :key="option"
            class="admin-chip"
            :class="{ 'admin-chip--active': status === option }"
            @click="status = option"
          >
            {{ option === 'all' ? 'All' : option }}
          </button>
        </div>
        <div class="flex items-center gap-2 text-xs text-slate-400">
          <span>Make:</span>
          <select v-model="makeFilter" class="filter-select">
            <option value="all">All</option>
            <option v-for="make in makes" :key="make" :value="make">{{ make }}</option>
          </select>
        </div>
        <div class="flex items-center gap-2 text-xs text-slate-400">
          <span>Model:</span>
          <select v-model="modelFilter" class="filter-select">
            <option value="all">All</option>
            <option v-for="model in models" :key="model" :value="model">{{ model }}</option>
          </select>
        </div>
      </div>

      <div v-if="loading" class="py-24 text-center text-slate-400">Loading equipment types…</div>
      <div v-else-if="error" class="py-24 text-center text-rose-400">{{ error }}</div>
      <div v-else-if="!filteredTypes.length" class="py-24 text-center text-slate-400">
        No equipment types match your filters.
      </div>
      <div v-else class="grid gap-4 grid-cols-[repeat(auto-fit,minmax(260px,1fr))]">
        <article
          v-for="type in filteredTypes"
          :key="type.id"
          class="group rounded-2xl border border-slate-700/60 bg-slate-950/82 p-4 shadow-[0_10px_22px_rgba(9,16,31,0.58),0_0_14px_rgba(188,226,255,0.12)] transition hover:border-primary-400/60 hover:bg-slate-900/75 hover:shadow-[0_20px_42px_rgba(9,16,31,0.6),0_0_28px_rgba(188,226,255,0.2)] hover:-translate-y-1"
          style="transition-property: transform, box-shadow, border-color, background-color;"
        >
          <div class="flex flex-col gap-4">
            <button class="w-full text-left cursor-pointer" @click="openDrawer(type)">
              <div class="flex items-start justify-between gap-4">
                <div class="space-y-2">
                  <div class="flex items-start gap-2">
                    <div class="flex-1">
                      <h2 class="text-lg text-white font-semibold tracking-tight">{{ type.name }}</h2>
                      <p class="text-xs text-slate-400 flex items-center gap-2">
                        <span>{{ type.make }} • {{ type.model }}</span>
                        <span v-if="type.descriptor" class="text-slate-500">— {{ type.descriptor }}</span>
                      </p>
                    </div>
                    <span
                      v-if="type.isAccessory"
                      class="inline-flex items-center rounded-md border border-slate-700/60 bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-slate-400"
                    >
                      Accessory
                    </span>
                  </div>

                  <div class="flex flex-wrap items-center gap-2 text-[11px] text-slate-300/90">
                    <span class="badge" :class="statusClass(type.status)">{{ type.status }}</span>
                    <span
                      v-if="type.products?.length"
                      class="inline-flex items-center gap-1 rounded-full border border-slate-700/60 bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-slate-400"
                    >
                      {{ type.products.join(', ') }}
                    </span>
                  </div>
                </div>

                <div class="flex items-center gap-2 text-xs text-primary-200 group-hover:text-primary-100">
                  <span>View</span>
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>

            <div class="flex items-center justify-end gap-2 text-xs">
              <button class="btn-secondary btn-secondary--xs cursor-pointer" @click="openDrawer(type)">Edit</button>
              <button class="btn-secondary btn-secondary--xs cursor-pointer" @click="duplicateType(type)">
                Duplicate
              </button>
              <button class="btn-danger btn-danger--xs cursor-pointer" @click="confirmDelete(type)">Delete</button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <EquipmentBulkUploadModal :open="bulkUploadOpen" @close="bulkUploadOpen = false" @imported="handleBulkImported" />
    <EquipmentTypeOnboarding
      :key="activeType ? `edit-${activeType.id}` : 'create'"
      v-if="onboardingOpen"
      :open="onboardingOpen"
      :inventory-types="types"
      :products="inventory.products"
      :draft-id="activeDraftId"
      :loading="onboardingSaving"
      :mode="activeType ? 'edit' : 'create'"
      :initial-data="activeTypePayload"
      :type-id="activeType?.id ?? null"
      :enable-drafts="!activeType"
      @close="closeOnboarding"
      @submit="handleOnboardingSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, nextTick } from 'vue'
import { useInventoryStore } from '@/stores/inventory'
import type { EquipmentTypeSummary, EquipmentTypeCreateInput } from '@/types/inventory'
import {
  listEquipmentTypes,
  createEquipmentTypeWithDetails,
  upsertEquipmentDraft,
  updateEquipmentTypeWithDetails,
  fetchEquipmentTypeDetail,
  deleteEquipmentType,
} from '@/lib/inventoryApi'
import EquipmentTypeOnboarding from '@/components/modals/EquipmentTypeOnboarding.vue'
import EquipmentBulkUploadModal from '@/components/modals/EquipmentBulkUploadModal.vue'
import { createLogger } from '@/lib/logger'

const log = createLogger('AdminEquipment')

const inventory = useInventoryStore()
const types = ref<EquipmentTypeSummary[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const search = ref('')
const status = ref<'all' | 'active' | 'archived' | 'draft'>('all')
const makeFilter = ref<'all' | string>('all')
const modelFilter = ref<'all' | string>('all')
const onboardingOpen = ref(false)
const onboardingSaving = ref(false)
const activeDraftId = ref<number | null>(null)
const activeType = ref<EquipmentTypeSummary | null>(null)
const prefillData = ref<EquipmentTypeCreateInput | null>(null)
const activeTypePayload = computed(() => (activeType.value ? mapTypeToPayload(activeType.value) : prefillData.value))

const statusFilters: Array<'all' | 'active' | 'archived' | 'draft'> = ['all', 'active', 'archived', 'draft']
const makes = computed(() => Array.from(new Set(types.value.map((type) => type.make).filter(Boolean))).sort())
const models = computed(() => {
  const source = makeFilter.value === 'all' ? types.value : types.value.filter((type) => type.make === makeFilter.value)
  return Array.from(new Set(source.map((type) => type.model).filter(Boolean))).sort()
})

const filteredTypes = computed(() => {
  const term = search.value.trim().toLowerCase()
  return types.value
    .filter((type) => {
    const matchesStatus = status.value === 'all' || type.status === status.value
      const matchesMake = makeFilter.value === 'all' || type.make === makeFilter.value
      const matchesModel = modelFilter.value === 'all' || type.model === modelFilter.value
    const matchesSearch =
      !term ||
      type.name.toLowerCase().includes(term) ||
      (type.descriptor ?? '').toLowerCase().includes(term) ||
      type.make.toLowerCase().includes(term) ||
      type.model.toLowerCase().includes(term)
      return matchesStatus && matchesSearch && matchesMake && matchesModel
    })
    .sort((a, b) => {
      const makeCompare = a.make.localeCompare(b.make)
      if (makeCompare !== 0) return makeCompare
      return a.model.localeCompare(b.model)
    })
})

function statusClass(state: string) {
  return {
    badge: true,
    'badge--success': state === 'active',
    'badge--warning': state === 'draft',
    'badge--muted': state === 'archived',
  }
}

async function loadTypes(force = false) {
  if (loading.value) return
  loading.value = true
  error.value = null
  try {
    if (force) {
      await inventory.loadTypes()
    } else if (!inventory.types.length) {
      await inventory.loadTypes()
    }
    types.value = await listEquipmentTypes()
  } catch (err) {
    log.error('loadTypes failed', err)
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
  }
}

function openCreate() {
  activeType.value = null
  prefillData.value = null
  activeDraftId.value = null
  onboardingOpen.value = true
}

async function openDrawer(type: EquipmentTypeSummary) {
  try {
    onboardingSaving.value = true
    const fullType = await fetchEquipmentTypeDetail(type.id)
    activeDraftId.value = null
    prefillData.value = null
    onboardingOpen.value = false
    await nextTick()
    activeType.value = fullType
    onboardingOpen.value = true
  } catch (error) {
    log.error('failed to load type detail', error)
  } finally {
    onboardingSaving.value = false
  }
}

function closeOnboarding() {
  onboardingOpen.value = false
  activeType.value = null
  prefillData.value = null
}

async function handleOnboardingSubmit(payload: EquipmentTypeCreateInput) {
  if (onboardingSaving.value) return
  onboardingSaving.value = true
  try {
    if (activeType.value) {
      await updateEquipmentTypeWithDetails(activeType.value.id, payload)
    } else {
      await createEquipmentTypeWithDetails(payload)
    }
    await loadTypes(true)
    onboardingOpen.value = false
  } catch (error) {
    log.error('submit failed', error)
  } finally {
    prefillData.value = null
    onboardingSaving.value = false
  }
}

async function handleOnboardingDraft(data: EquipmentTypeCreateInput) {
  const draft = await upsertEquipmentDraft({ id: activeDraftId.value ?? undefined, ...data })
  activeDraftId.value = draft.id
  await loadTypes(true)
  return { id: draft.id }
}

async function duplicateType(type: EquipmentTypeSummary) {
  try {
    const copy = mapTypeToPayload(type)
    copy.status = 'draft'
    copy.name = `${type.name} Copy`
    prefillData.value = copy
    activeType.value = null
    activeDraftId.value = null
    onboardingOpen.value = false
    await nextTick()
    onboardingOpen.value = true
  } catch (error) {
    log.error('duplicate failed', error)
    toast('Unable to duplicate equipment type', 'error')
  }
}

async function confirmDelete(type: EquipmentTypeSummary) {
  const ok = window.confirm(`Delete equipment type “${type.name}”? This cannot be undone.`)
  if (!ok) return
  try {
    await deleteEquipmentType(type.id)
    toast('Equipment type deleted', 'success')
    await loadTypes(true)
  } catch (error) {
    log.error('delete failed', error)
    toast('Failed to delete equipment type', 'error')
  }
}

function toast(message: string, type: 'success' | 'error' = 'success') {
  try {
    window.dispatchEvent(new CustomEvent('toast', { detail: { type, message } }))
  } catch {}
}

function mapTypeToPayload(type: EquipmentTypeSummary): EquipmentTypeCreateInput {
  return {
    status: type.status,
    name: type.name,
    make: type.make,
    model: type.model,
    descriptor: type.descriptor ?? undefined,
    categoryId: type.categoryId ?? undefined,
    isAccessory: type.isAccessory,
    products: type.products,
    specs: type.specs ?? undefined,
    assets: type.assets ?? undefined,
    accessories: type.accessories ?? undefined,
  }
}

const bulkUploadOpen = ref(false)

function openBulkUpload() {
  bulkUploadOpen.value = true
}

function handleBulkImported() {
  bulkUploadOpen.value = false
  loadTypes(true)
}

onMounted(() => {
  loadTypes()
})
</script>

<style scoped>
.admin-chip {
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  border: 1px solid rgba(71, 85, 105, 0.7);
  background: rgba(15, 23, 42, 0.8);
  color: rgba(148, 163, 184, 0.85);
  transition: border 0.2s ease, color 0.2s ease;
}

.admin-chip--active {
  border-color: rgba(14, 165, 233, 0.6);
  color: rgba(125, 211, 252, 0.95);
  background: rgba(14, 165, 233, 0.12);
}

.mini-stat {
  background: rgba(30, 41, 59, 0.4);
  border-radius: 0.75rem;
  padding: 0.75rem;
}

.badge {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(71, 85, 105, 0.6);
  padding: 0.125rem 0.5rem;
  border-radius: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
}

.badge--outline {
  border-color: rgba(51, 65, 85, 0.6);
  color: rgba(203, 213, 225, 0.85);
}

.badge--success {
  border-color: rgba(16, 185, 129, 0.5);
  color: rgba(167, 243, 208, 0.95);
  background: rgba(16, 185, 129, 0.12);
}

.badge--warning {
  border-color: rgba(245, 158, 11, 0.5);
  color: rgba(253, 230, 138, 0.95);
  background: rgba(245, 158, 11, 0.12);
}

.badge--muted {
  border-color: rgba(51, 65, 85, 0.6);
  color: rgba(148, 163, 184, 0.8);
}

.filter-select {
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(71, 85, 105, 0.6);
  color: rgba(203, 213, 225, 0.85);
  border-radius: 0.5rem;
  padding: 0.25rem 0.5rem;
}
</style>

